import { APP_NAME, RequestMethod } from '~/shared/constants/commonConstants'
import { ApiService } from '~/shared/services/api'
import { IRequestOptions, IResponseResult } from '~/shared/types/apiTypes'
import {
  IBaseSubscriptionInfoRaw,
  IInAppPurchaseResponseRaw,
  IPrimerClientSessionResponseRaw,
  IPrimerPaymentResponseRaw,
  IPurchaseResponseRaw,
  ISecondaryResponseRaw,
  ISubscriptionInfoRaw,
} from '~/shared/types/paymentTypes'

export class PrimerPaymentApi {
  private readonly baseUrl = 'v2/purchases'
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  getClientSessionToken({
    uuid,
    cohort,
    planId,
    trialPeriodDays,
  }: {
    uuid: string
    cohort: string
    trialPeriodDays?: number
    planId: number
  }): Promise<IResponseResult<IPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        cohort,
        plan_id: planId,
        trial_duration: trialPeriodDays,
      },
    }

    return this.api.makeRequest<IPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/token`,
      options,
    )
  }

  createSubscription({
    uuid,
    token,
  }: {
    uuid: string
    token: string
  }): Promise<IResponseResult<IPrimerPaymentResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_method_token: token,
      },
    }
    return this.api.makeRequest<IPurchaseResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/payment`,
      options,
    )
  }

  resumeSubscription({
    uuid,
    paymentId,
    resumeToken,
  }: {
    uuid: string
    paymentId: string
    resumeToken: string
  }): Promise<IResponseResult<IPrimerPaymentResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
        resume_token: resumeToken,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/payment/resume`,
      options,
    )
  }

  confirmSubscription({
    uuid,
    paymentId,
  }: {
    uuid: string
    paymentId: string
  }): Promise<
    IResponseResult<{
      status: boolean
      purchase: ISubscriptionInfoRaw
      error?: boolean
    }>
  > {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/confirm`,
      options,
    )
  }

  updateClientSession({
    uuid,
    paymentId,
    clientToken,
  }: {
    uuid: string
    paymentId?: string
    clientToken: string
  }): Promise<IResponseResult<IPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.PATCH,
      body: {
        ...(paymentId && { payment_id: paymentId }),
        client_token: clientToken,
      },
    }
    return this.api.makeRequest<IPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/token`,
      options,
    )
  }

  makeUpsell({
    uuid,
    planId,
    productId,
    promoCode,
  }: {
    uuid: string
    planId: number
    productId: string
    promoCode?: string
  }): Promise<IResponseResult<IBaseSubscriptionInfoRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        description: productId,
        promo_code: promoCode,
        plan_id: planId,
      },
    }

    return this.api.makeRequest<IBaseSubscriptionInfoRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/upsell`,
      options,
    )
  }

  makeSecondary({
    uuid,
    planId,
    productId,
    promoCode,
  }: {
    uuid: string
    planId: number
    productId: string
    promoCode?: string
  }): Promise<IResponseResult<ISecondaryResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        description: productId,
        promo_code: promoCode,
        plan_id: planId,
      },
    }

    return this.api.makeRequest<ISecondaryResponseRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/secondary`,
      options,
    )
  }

  createInAppPurchase({
    uuid,
    planId,
    productName,
  }: {
    uuid: string
    planId: number
    productName?: string
  }): Promise<IResponseResult<IInAppPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        product_name: productName,
      },
    }

    return this.api.makeRequest<IInAppPurchaseResponseRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/inapp`,
      options,
    )
  }
}
